import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import CreateEditGroupModal from 'Components/CreateEditGroup/CreateEditGroupModal';
import useUpdateRuleGroup from 'Hooks/query-hooks/global-rules/useUpdateRuleGroup';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import { RQ_GLOBAL_RULES, RQ_RULE_GROUPS } from 'Utils';

type Props = {
    isEditOpen: boolean;
    rulesGroupData?: RuleGroup[];
    groupData?: {
        key: string;
        displayValue: string;
    }[];
    closeEditModal(): void;
    onDeleteAction(): void;
    selectedGroup: string;
    isDeleteGroupOption: boolean;
    deleteGroup?: EmptyRuleGroup;
    emptyGroupsData?: EmptyRuleGroup[];
};

const UpdateRuleGroups = ({
    isEditOpen,
    deleteGroup,
    groupData,
    closeEditModal,
    onDeleteAction,
    selectedGroup,
    isDeleteGroupOption,
    rulesGroupData,
    emptyGroupsData
}: Props) => {
    const { addAlert } = useAlertBanner();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { templateRepositoryId } = useTemplateStore.getState().template;

    const mutation = useUpdateRuleGroup(
        isDeleteGroupOption && deleteGroup
            ? Number(emptyGroupsData?.find((group) => group.name === selectedGroup)?.id)
            : Number(rulesGroupData?.find((group) => group.name === selectedGroup)?.adhocGroupId),
        {
            mutationOptions: {
                onSuccess: () => {
                    closeEditModal();
                    addAlert({
                        id: 'update-group-success',
                        content: t('ruleGroupUpdatedSuccessfully'),
                        status: 'success'
                    });
                    queryClient.invalidateQueries([RQ_RULE_GROUPS]);
                    queryClient.invalidateQueries([RQ_GLOBAL_RULES, templateRepositoryId]);
                },
                onError: (err: { message: string; statusCode: number }[]) => {
                    const statusText = err[0]?.message || t('Try Later');
                    addAlert({
                        id: 'update-group-success',
                        content: statusText,
                        status: 'danger'
                    });
                    closeEditModal();
                }
            }
        }
    );
    return (
        <>
            <CreateEditGroupModal
                isOpen={isEditOpen}
                closeModal={() => closeEditModal()}
                existingGroups={groupData ? groupData.map((group) => group.displayValue) : []}
                selectedGroupName={selectedGroup}
                deleteGroup={deleteGroup}
                isDeleteGroupOption={isDeleteGroupOption}
                onDelete={() => {
                    onDeleteAction();
                    closeEditModal();
                }}
                mutation={mutation}
            />
        </>
    );
};

export default React.memo(UpdateRuleGroups);
